import { getRandomInvoiceNumber, getRandomNumberbyMaxLength } from "@/utils/payments";
import { mapRoundingData } from "../mappers/CharityRoundingTypesMapper";
import { FREEDOMPAY_GATEWAY_ID, roundOffByBankApproach } from "../../src/utils";

export const mapOAPIItems = (cart) => {
  return cart.cart_items.map(({ cart_item_with_root_map_node: item }) => {
    return {
      id: item.item.item.id,
      sku: item.item.item.sku,
      name: item.item.item.name,
      unitCost: item.cart_item_cost.unit_cost,
      discounts: item.discounts,
      quantity: item.quantity,
      mods: item.item.map_node_item
    };
  });
};
class CalculateCartDTO {
  static toOAPI(payload, cartItems, promoCode = "", dispatch, rootGetters) {
    const discounts = [],
      payments = [];
    const gatewayId = rootGetters["vendor/tokenizedGatewayId"];
    const deviceOrderId =
      gatewayId === FREEDOMPAY_GATEWAY_ID ? getRandomInvoiceNumber() : getRandomNumberbyMaxLength(18).toString();
    const appliedAmenities = rootGetters["cart/getAppliedAmenities"].amenities;

    const roundingData = {
      amount: rootGetters["cart/getRoundUpForCharityAmount"],
      roundUpForCharity: rootGetters["cart/getRoundUpForCharity"],
      hash: rootGetters["vendor/getActiveVendor"].charityRoundingType.hash
    };

    let firstName = "",
      lastName = "",
      email = "",
      phone = "";

    // Save the deviceOrderId into the store to use it on Order confirmation (for qr-code)
    dispatch("setDeviceOrderId", deviceOrderId);

    //If we get a promo code we store it in OAPI cart this way the OAPICart promo
    //code doesn't get overriden when no promo code is passed
    if (promoCode) {
      dispatch("OAPICart", { promoCode });
    }
    const cartItemsPayload = cartItems?.map((i) => ({
      cart_item_with_root_map_node: {
        item: CalculateCartDTO.mapItemRMN(i.item),
        quantity: i.quantity,
        activity: "ACTIVITY_PURCHASE",
        cart_item_cost: {
          original_total: i.activePriceLevel.Cost * i.quantity,
          original_unit_cost: i.activePriceLevel.Cost,
          total: i.activePriceLevel.Cost * i.quantity,
          unit_cost: i.activePriceLevel.Cost
        },
        map_node_items: i.modifiers
      }
    }));

    //Get user and guest user data
    const userData = rootGetters["user/getUserData"];
    const guestUserData = rootGetters["user/getGuestUserData"];

    //if user is logged in
    if (userData) {
      firstName = userData.profile.firstName;
      lastName = userData.profile.lastName;
      email = userData.profile.email;
      phone = userData.profile.phone;
    }
    //if guest is logged in
    if (guestUserData) {
      firstName = guestUserData.name;
      lastName = "";
      email = guestUserData.email;
      //to format the same way as what we keep in state for a logged used
      phone = guestUserData.phone.replace(/[^0-9]/g, "");
    }

    //Discounts processing
    //Push a single discount from the OAPICart promo code when available
    if (rootGetters["cart/getOAPICart"].promoCode) {
      discounts.push({
        discount_type: rootGetters["cart/getOAPICart"].promoCode
      });
    }

    //Loop thru ppi reductions and apply them as discounts
    const reductions = rootGetters["cart/getPpiReductions"];
    reductions.forEach((ppiReduction) => {
      ppiReduction.reductions.forEach((reduction) => {
        discounts.push({ discount_type: String(reduction.reductionId) });
      });
    });

    if (appliedAmenities.length && appliedAmenities.length > 0) {
      appliedAmenities.forEach((amenity) => {
        payments.push({
          payment_type: amenity.selectedTender.paymentType,
          subpayment_type: amenity.selectedTender.subpaymentType,
          ppi_loyalty_id: amenity.card,
          ppi_pin: ""
        });
      });
    }

    return {
      cart: {
        device_order_id: deviceOrderId,
        customer: {
          external_id: "",
          first_name: firstName,
          last_name: lastName,
          email: email,
          address1: "",
          address2: "",
          city: "",
          postal_code: "",
          state: "",
          notes: "",
          phone_number: phone
        },
        cart_items: cartItemsPayload,
        subtotal: roundOffByBankApproach(payload.cart.subtotalAmount),
        calculated_cart_sum: [
          {
            value: roundOffByBankApproach(payload.cart.subtotalAmount)
          }
        ],
        location: {
          employee_id: rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID,
          station_id: `${payload.vendorId}`,
          store_id: `${payload.venueId}`
        },
        payments,
        visitor_id: rootGetters["user/getVisitorId"] || "",
        seat_id: String(rootGetters["level/getCurrentSelection"]?.seatId || ""),
        tip_status_closed: true,
        pickup_time: String(rootGetters["orderAhead/selectedPickup"] || ""),
        rounding: mapRoundingData(roundingData)
      },
      user: { discounts }
    };
  }

  /**
   * Additional type casting of non-nullable props in API
   *
   * @param {ItemDTO} item
   * @returns ItemDTO
   */
  static mapItemRMN(item) {
    return {
      ...item,
      root_map_node: item["root_map_node"] === null ? {} : { ...item["root_map_node"] }
    };
  }

  static fromOAPI(response, getters, dispatch) {
    const cart = response.signed_cart.cart.cart_with_modifier_items;

    const cartItems = getters["getCartItems"];
    const items = mapOAPIItems(cart);
    const feesDetail = cart.fees.map((fee) => {
      return {
        name: fee.name,
        value: fee.calculated_value
      };
    });

    const itemLevelAppliedDiscounts = cart.cart_items.reduce(
      (discounts, item) => discounts.concat(item.cart_item_with_root_map_node.discounts),
      []
    );

    dispatch("OAPICart", {
      items,
      deviceOrderUuid: cart.device_order_uuid,
      subtotal: cart.cart_totals.subtotal,
      taxTotal: cart.cart_totals?.exclusive_tax,
      fees: cart.cart_totals?.fees_total,
      feesDetail: feesDetail,
      discounts: cart.cart_totals?.discounts_total,
      total: cart.cart_totals?.total,
      cartExpirationTime: Number(response.signed_cart.exp) * 1000,
      appliedDiscounts: itemLevelAppliedDiscounts.concat(cart.discounts)
    });

    return {
      cart: {
        items: cartItems.map((i) => ({
          item: {
            subvendorId: i.item.subvendorId,
            id: i.item.id,
            name: i.item.name,
            sku: i.item.sku,
            unitCost: i.item.cost,
            type: i.item.type
          },
          notes: i.item.notes,
          quantity: i.quantity,
          totalCost: 0
        })),
        subtotalAmount: cart.cart_totals.subtotal
      },
      promoInfo: { promoCodeApplied: false },
      tax: cart.cart_totals?.exclusive_tax,
      total: cart.cart_totals.total
    };
  }
}

export default CalculateCartDTO;
